import _ from 'lodash';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import moment from 'moment-timezone';

import CallSection from '../CallSection';
import NoAppointments from '../NoAppointments';
import TimeSlots from '../TimeSlots';

import { getFormattedDate, PracticeSlotsGroup, ViewProps } from '../common';
import { constructAddress } from '../../../../utils/practice';
import { ReactComponent as MapPinSvg } from '../../../../assets/map-pin.svg';

import * as api from '../../../../api';

import styles from '../../index.module.css';

const PracticeView: React.FC<ViewProps> = ({
  availableEhrTimeSlots,
  practices,
  navigateToNextPage,
  onAppointmentSelected,
  practice,
  encounter,
  fieldConfig,
}) => {
  const { t } = useTranslation();
  const groupsByPractice = useMemo<PracticeSlotsGroup[]>(() => {
    return Object.keys(practices)
      .map((practiceId) => {
        const timeSlots = availableEhrTimeSlots.filter(
          (slot) => slot.practiceId === practiceId,
        );
        return {
          practice: {
            ...practices[practiceId],
            id: practiceId,
          },
          timeSlots,
        };
      })
      .filter((practiceGroup) => practiceGroup.timeSlots.length);
  }, [availableEhrTimeSlots, practices]);

  const renderPracticeAppointments = (
    slots: api.EhrTimeSlot.EhrTimeSlot[],
    practice: api.EhrTimeSlot.PracticeTimeSlot,
  ) => {
    const dayGroup = _.groupBy(slots, (apt) =>
      practice.timezone
        ? moment(apt.startTime).tz(practice.timezone).startOf('day').format()
        : moment(apt.startTime).startOf('day').format(),
    );

    return (
      <>
        {Object.keys(dayGroup).map((dayTime) => {
          return (
            <div key={dayTime}>
              <div className={styles.PracticeDateWrapper}>
                {getFormattedDate(dayTime, practice.timezone, t)}
              </div>
              <div className={styles.RenderAppointmentDisplay}>
                <TimeSlots
                  timeSlots={dayGroup[dayTime]}
                  timezone={practice.timezone}
                  onAppointmentSelected={onAppointmentSelected}
                />
              </div>
            </div>
          );
        })}
      </>
    );
  };

  if (!groupsByPractice.length) {
    return (
      <NoAppointments
        navigateToNextPage={navigateToNextPage}
        practice={practice}
        encounter={encounter}
        fieldConfig={fieldConfig}
      />
    );
  }

  return (
    <>
      {groupsByPractice.map((practiceSlotsGroup: PracticeSlotsGroup, index) => {
        const { practice, timeSlots } = practiceSlotsGroup;
        return (
          <div
            className={classNames(styles.Practice, {
              [styles.BorderBottom]:
                fieldConfig.scheduleAppointmentCallSectionEnabled ||
                index + 1 !== groupsByPractice.length,
            })}
            key={practice.id}
          >
            <div>
              <div className={styles.PracticeName}>{practice.name}</div>
              <div className={styles.PracticeDetails}>
                <div>
                  <MapPinSvg />
                  {constructAddress(_.omit(practice, 'zip'))}
                </div>
              </div>
            </div>
            <div>{renderPracticeAppointments(timeSlots, practice)}</div>
          </div>
        );
      })}
      {fieldConfig.previsitFormFieldId &&
        fieldConfig.scheduleAppointmentCallSectionEnabled &&
        practice && (
          <CallSection
            navigateToNextPage={navigateToNextPage}
            practice={practice}
            previsitFormFieldId={fieldConfig.previsitFormFieldId}
            label={t('Not finding your ideal availability?')}
          />
        )}
    </>
  );
};

export default PracticeView;
